import React, { useState, useRef, useEffect } from "react";
import api from "../../api";
import { FiUpload, FiCamera, FiSearch, FiSave } from "react-icons/fi";
import TextFieldMapper from "./TextFieldMapper";
import { compressImage, isMobileDevice } from "./imageCompression";
import { detectCard, getDetectionMessage } from "./cardDetection";
import "./BusinessCardScannerPanel.css";

const BusinessCardScannerPanel = ({ onClose, onSave, showToast }) => {
  const [step, setStep] = useState("upload"); // 'upload', 'processing', 'map', 'review'
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [ocrLines, setOcrLines] = useState([]);
  const [scannedImage, setScannedImage] = useState(null);
  const [editedData, setEditedData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    role_title: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    linkedin: "",
    domain: "",
    source: "Business Card Scan",
  });
  const [isUsingCamera, setIsUsingCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const [captureCountdown, setCaptureCountdown] = useState(null);
  const [detectionStatus, setDetectionStatus] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const detectionCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const captureTimeoutRef = useRef(null);
  const detectionIntervalRef = useRef(null);
  const detectionStableCountRef = useRef(0);

  // Cleanup camera stream
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (captureTimeoutRef.current) {
        clearTimeout(captureTimeoutRef.current);
      }
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
    };
  }, [stream]);

  // Handle video stream attachment and start card detection
  useEffect(() => {
    if (stream && videoRef.current && isUsingCamera) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(err => {
        console.error("Error playing video:", err);
      });
      
      // Start card detection after video is ready
      const startDetection = () => {
        if (detectionIntervalRef.current) {
          clearInterval(detectionIntervalRef.current);
        }
        
        // Check for card every 300ms
        detectionIntervalRef.current = setInterval(() => {
          if (videoRef.current && detectionCanvasRef.current && captureCountdown === null) {
            const result = detectCard(videoRef.current, detectionCanvasRef.current);
            setDetectionStatus(result);
            
            // If card detected, increment stable counter
            if (result.detected) {
              detectionStableCountRef.current += 1;
              
              // If stable for 3 checks (~900ms), start countdown
              if (detectionStableCountRef.current >= 3) {
                console.log('Card detected and stable, starting capture countdown');
                setCaptureCountdown(2); // 2 second countdown
                detectionStableCountRef.current = 0;
              }
            } else {
              // Reset stable counter if card not detected
              detectionStableCountRef.current = 0;
            }
          }
        }, 300);
      };
      
      // Wait for video to be ready
      if (videoRef.current.readyState >= 2) {
        startDetection();
      } else {
        videoRef.current.addEventListener('loadeddata', startDetection, { once: true });
      }
    }
    
    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
    };
  }, [stream, isUsingCamera, captureCountdown]);

  // Countdown effect for auto-capture
  useEffect(() => {
    if (captureCountdown !== null && captureCountdown > 0) {
      captureTimeoutRef.current = setTimeout(() => {
        setCaptureCountdown(captureCountdown - 1);
      }, 1000);
    } else if (captureCountdown === 0) {
      // Auto capture when countdown reaches 0
      capturePhoto();
      setCaptureCountdown(null);
      detectionStableCountRef.current = 0;
    }
    
    return () => {
      if (captureTimeoutRef.current) {
        clearTimeout(captureTimeoutRef.current);
      }
    };
  }, [captureCountdown]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      try {
        const isMobile = isMobileDevice();
        let processedFile = file;
        
        if (isMobile || file.size > 2 * 1024 * 1024) {
          showToast("Compressing image...", "info");
          processedFile = await compressImage(file, {
            maxWidth: 1920,
            maxHeight: 1920,
            quality: 0.85,
            maxSizeMB: 2
          });
          showToast("Image compressed successfully", "success");
        }
        
        setSelectedFile(processedFile);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(processedFile);
      } catch (error) {
        console.error("Error processing image:", error);
        showToast("Failed to process image. Please try again.", "error");
      }
    } else {
      showToast("Please select a valid image file", "error");
    }
  };

  const startCamera = async () => {
    try {
      const constraints = {
        video: { 
          facingMode: "environment",
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 }
        }
      };
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setIsUsingCamera(true);
      setStream(mediaStream);
      setCaptureCountdown(null);
      setDetectionStatus(null);
      detectionStableCountRef.current = 0;
    } catch (error) {
      console.error("Error accessing camera:", error);
      showToast("Unable to access camera. Please check permissions.", "error");
    }
  };

  const capturePhoto = async () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob(async (blob) => {
        try {
          let file = new File([blob], "business-card.jpg", { type: "image/jpeg" });
          
          if (file.size > 2 * 1024 * 1024) {
            showToast("Optimizing image...", "info");
            file = await compressImage(file, {
              maxWidth: 1920,
              maxHeight: 1920,
              quality: 0.85,
              maxSizeMB: 2
            });
          }
          
          setSelectedFile(file);
          setImagePreview(canvas.toDataURL("image/jpeg", 0.85));
          
          if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
          }
          if (detectionIntervalRef.current) {
            clearInterval(detectionIntervalRef.current);
          }
          setIsUsingCamera(false);
          setCaptureCountdown(null);
          setDetectionStatus(null);
          detectionStableCountRef.current = 0;
        } catch (error) {
          console.error("Error processing captured photo:", error);
          showToast("Failed to process photo. Please try again.", "error");
        }
      }, "image/jpeg", 0.92);
    }
  };

  const cancelCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }
    setIsUsingCamera(false);
    setCaptureCountdown(null);
    setDetectionStatus(null);
    detectionStableCountRef.current = 0;
    if (captureTimeoutRef.current) {
      clearTimeout(captureTimeoutRef.current);
    }
  };

  const handleScan = async () => {
    if (!selectedFile) {
      showToast("Please select or capture an image first", "error");
      return;
    }

    setStep("processing");

    try {
      const formData = new FormData();
      formData.append("businessCard", selectedFile);

      console.log("Uploading business card:", {
        fileName: selectedFile.name,
        fileSize: `${(selectedFile.size / 1024).toFixed(2)} KB`,
        fileType: selectedFile.type
      });

      const timeoutMs = isMobileDevice() ? 60000 : 30000;

      const response = await api.post("/business-card/scan", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: timeoutMs,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });

      if (response.data.success) {
        console.log("OCR Response received:", {
          linesCount: response.data.lines?.length,
          hasImage: !!response.data.image,
          parsedData: response.data.parsedData
        });
        
        setExtractedData(response.data);
        setOcrLines(response.data.lines || []);
        setScannedImage(response.data.image);
        setEditedData({
          ...editedData,
          ...response.data.parsedData,
          source: "Business Card Scan",
        });
        setStep("map");
        showToast("Business card scanned successfully!", "success");
      } else {
        throw new Error("Failed to scan business card");
      }
    } catch (error) {
      console.error("Error scanning business card:", error);
      
      let errorMessage = "Failed to scan business card. Please try again.";
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = "Request timed out. Please check your internet connection and try again.";
      } else if (error.response) {
        errorMessage = error.response.data?.error || error.response.data?.details || errorMessage;
      } else if (error.request) {
        errorMessage = "Network error. Please check your internet connection.";
      }
      
      showToast(errorMessage, "error");
      setStep("upload");
    }
  };

  const handleInputChange = (field, value) => {
    setEditedData({
      ...editedData,
      [field]: value,
    });
  };

  const handleSaveContact = async () => {
    if (!editedData.name && !editedData.email && !editedData.phone) {
      showToast("Please provide at least a name, email, or phone number", "error");
      return;
    }

    try {
      const dataToSave = Object.entries(editedData).reduce((acc, [key, value]) => {
        if (value && value.trim() !== "") {
          acc[key] = value.trim();
        }
        return acc;
      }, {});

      await onSave(dataToSave);
      showToast("Contact saved successfully!", "success");
      onClose();
    } catch (error) {
      console.error("Error saving contact:", error);
      showToast("Failed to save contact. Please try again.", "error");
    }
  };

  const handleBack = () => {
    if (step === "review") {
      setStep("map");
    } else {
      setStep("upload");
      setExtractedData(null);
      setOcrLines([]);
      setScannedImage(null);
    }
  };

  const handleFieldDataChange = (fieldKey, value) => {
    setEditedData({
      ...editedData,
      [fieldKey]: value,
    });
  };

  const proceedToReview = () => {
    setStep("review");
  };

  return (
    <div className="business-card-scanner-panel">
      <div className="panel-header">
        <h2>Scan Business Card</h2>
        <button className="close-btn" onClick={onClose} title="Close">×</button>
      </div>

      <div className="panel-content">
        {step === "upload" && !isUsingCamera && (
          <div className="upload-section">
            <div className="image-preview-container">
              {imagePreview ? (
                <img src={imagePreview} alt="Business card preview" className="image-preview" />
              ) : (
                <div className="placeholder">
                  <FiCamera size={64} color="#999" />
                  <p>No image selected</p>
                </div>
              )}
            </div>
            
            <div className="upload-buttons">
              <button
                className="btn-primary"
                onClick={() => fileInputRef.current?.click()}
              >
                <FiUpload size={16} style={{ marginRight: '8px' }} />
                Upload Image
              </button>
              <button
                className="btn-secondary"
                onClick={startCamera}
              >
                <FiCamera size={16} style={{ marginRight: '8px' }} />
                Use Camera
              </button>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            
            {selectedFile && (
              <div className="selected-file-info">
                <p><strong>Selected:</strong> {selectedFile.name}</p>
                <button className="btn-primary" onClick={handleScan}>
                  <FiSearch size={16} style={{ marginRight: '8px' }} />
                  Scan Business Card
                </button>
              </div>
            )}
          </div>
        )}

        {step === "upload" && isUsingCamera && (
          <div className="camera-section">
            <div className="camera-container">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="camera-video"
              />
              
              {/* Camera Guide Overlay */}
              <div className="camera-guide-overlay">
                <div className={`camera-guide-frame ${detectionStatus?.detected ? 'detected' : ''}`}>
                  <div className="guide-corner guide-corner-tl"></div>
                  <div className="guide-corner guide-corner-tr"></div>
                  <div className="guide-corner guide-corner-bl"></div>
                  <div className="guide-corner guide-corner-br"></div>
                  
                  {/* Detection Status Message */}
                  {detectionStatus && (
                    <div 
                      className="guide-text"
                      style={{ 
                        backgroundColor: detectionStatus.detected 
                          ? 'rgba(76, 175, 80, 0.9)' 
                          : 'rgba(0, 0, 0, 0.7)'
                      }}
                    >
                      {getDetectionMessage(detectionStatus).text}
                    </div>
                  )}
                </div>
                
                {/* Countdown Display */}
                {captureCountdown !== null && captureCountdown > 0 && (
                  <div className="capture-countdown">{captureCountdown}</div>
                )}
              </div>
            </div>
            
            <canvas ref={canvasRef} style={{ display: "none" }} />
            <canvas ref={detectionCanvasRef} style={{ display: "none" }} />
            
            <div className="camera-hint">
              <p>📷 Position your business card in the frame - we'll capture it automatically!</p>
            </div>
            
            <div className="camera-buttons">
              <button 
                className="btn-primary" 
                onClick={capturePhoto}
                disabled={captureCountdown !== null}
              >
                <FiCamera size={16} style={{ marginRight: '8px' }} />
                Capture Now
              </button>
              <button className="btn-secondary" onClick={cancelCamera}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {step === "processing" && (
          <div className="processing-section">
            <div className="spinner"></div>
            <p>Processing business card...</p>
            <p className="processing-hint">This may take a few moments</p>
          </div>
        )}

        {step === "map" && (
          <div className="mapping-section">
            {scannedImage && ocrLines.length > 0 ? (
              <>
                <TextFieldMapper
                  image={scannedImage}
                  lines={ocrLines}
                  parsedData={editedData}
                  onDataChange={handleFieldDataChange}
                />
                <div className="mapping-actions">
                  <button className="btn-primary" onClick={proceedToReview}>
                    Continue to Review →
                  </button>
                  <button className="btn-secondary" onClick={handleBack}>
                    Back
                  </button>
                </div>
              </>
            ) : (
              <div className="mapping-error">
                <h3>⚠️ Interactive Mapping Unavailable</h3>
                <p style={{ margin: '15px 0' }}>
                  The OCR didn't detect individual text lines for interactive mapping.
                </p>
                
                {/* Show the scanned image */}
                {scannedImage && (
                  <div style={{ marginBottom: '20px' }}>
                    <strong>Scanned Image:</strong>
                    <div style={{ 
                      marginTop: '10px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      maxWidth: '100%'
                    }}>
                      <img 
                        src={scannedImage} 
                        alt="Scanned business card" 
                        style={{ 
                          width: '100%', 
                          height: 'auto',
                          display: 'block'
                        }} 
                      />
                    </div>
                  </div>
                )}
                
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>
                  Image: {scannedImage ? '✓' : '✗'} | 
                  Text Lines with coordinates: {ocrLines.length}
                  {extractedData?.rawText && ` | Raw text extracted: ${extractedData.rawText.length} chars`}
                </p>
                
                {extractedData?.rawText && (
                  <div style={{ textAlign: 'left', marginBottom: '20px' }}>
                    <strong>Extracted Text:</strong>
                    <pre style={{ 
                      background: '#f5f5f5', 
                      padding: '10px', 
                      borderRadius: '4px',
                      fontSize: '12px',
                      maxHeight: '150px',
                      overflow: 'auto'
                    }}>
                      {extractedData.rawText}
                    </pre>
                  </div>
                )}
                
                <p style={{ marginBottom: '20px' }}>
                  We've auto-extracted what we could. You can review and edit the fields manually.
                </p>
                
                <button className="btn-primary" onClick={proceedToReview}>
                  Continue to Manual Review →
                </button>
                <button className="btn-secondary" onClick={handleBack} style={{ marginTop: '10px' }}>
                  ← Try Different Image
                </button>
              </div>
            )}
          </div>
        )}

        {step === "review" && (
          <div className="review-section">
            <div className="review-header">
              <h4>Review Extracted Information</h4>
              <p className="review-hint">Edit any fields as needed before saving</p>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={editedData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Full name"
                />
              </div>

              <div className="form-group">
                <label>Company</label>
                <input
                  type="text"
                  value={editedData.company}
                  onChange={(e) => handleInputChange("company", e.target.value)}
                  placeholder="Company name"
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editedData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="email@example.com"
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={editedData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  placeholder="(555) 555-5555"
                />
              </div>

              <div className="form-group">
                <label>Title/Role</label>
                <input
                  type="text"
                  value={editedData.role_title}
                  onChange={(e) => handleInputChange("role_title", e.target.value)}
                  placeholder="Job title"
                />
              </div>

              <div className="form-group">
                <label>Website/Domain</label>
                <input
                  type="text"
                  value={editedData.domain}
                  onChange={(e) => handleInputChange("domain", e.target.value)}
                  placeholder="company.com"
                />
              </div>

              <div className="form-group full-width">
                <label>Street Address</label>
                <input
                  type="text"
                  value={editedData.street}
                  onChange={(e) => handleInputChange("street", e.target.value)}
                  placeholder="123 Main St"
                />
              </div>

              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={editedData.city}
                  onChange={(e) => handleInputChange("city", e.target.value)}
                  placeholder="City"
                />
              </div>

              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  value={editedData.state}
                  onChange={(e) => handleInputChange("state", e.target.value)}
                  placeholder="CA"
                  maxLength="2"
                />
              </div>

              <div className="form-group">
                <label>ZIP Code</label>
                <input
                  type="text"
                  value={editedData.zip}
                  onChange={(e) => handleInputChange("zip", e.target.value)}
                  placeholder="12345"
                />
              </div>

              <div className="form-group">
                <label>LinkedIn</label>
                <input
                  type="text"
                  value={editedData.linkedin}
                  onChange={(e) => handleInputChange("linkedin", e.target.value)}
                  placeholder="linkedin.com/in/username"
                />
              </div>
            </div>

            {extractedData?.rawText && (
              <details className="raw-text-section">
                <summary>View Raw Extracted Text</summary>
                <pre>{extractedData.rawText}</pre>
              </details>
            )}

            <div className="review-actions">
              <button className="btn-primary" onClick={handleSaveContact}>
                <FiSave size={16} style={{ marginRight: '8px' }} />
                Save Contact
              </button>
              <button className="btn-secondary" onClick={handleBack}>
                ← Back to Mapping
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessCardScannerPanel;

